/**
 * Diagnóstico idioma + template en consola (Explorer / DevTools).
 * Indica Estado: OK o PROBLEMAS / Avisos. Usa globalThis['console'][...] para prod minify.
 */

import i18n from "@/i18n/config";
import {
  hasI18nUserLanguageChoice,
  I18N_USER_CHOICE_KEY,
  readEdgeCountryCookie,
  readEdgeLanguageRaw,
} from "@/lib/i18nEdgeSync";
import {
  readLangQueryParam,
  readPersistedUserLanguage,
} from "@/lib/languagePolicy";
import {
  normalizeCountryCode,
  usesSpanishDefaultForSite,
} from "@/lib/siteGeoPolicy";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { getEffectiveSiteLanguage } from "@/lib/siteLanguage";
import { BRAND_STORAGE_PREFIX } from "@/lib/brandConfig";

const STORAGE_KEY = `${BRAND_STORAGE_PREFIX}_site_debug`;
const SESSION_KEY = `${BRAND_STORAGE_PREFIX}_site_debug_session`;

let autoConsoleReportDone = false;

export type SiteLanguageHealth = {
  ok: boolean;
  issues: string[];
  warnings: string[];
};

export type SiteLanguageDebugPayload = {
  at: string;
  href: string;
  templateConfig: ReturnType<typeof getServerTemplateConfig>;
  usesSpanishDefaultForSite: boolean;
  user_country_cookie: string | null;
  edgeLanguageRaw: string | null;
  user_language_localStorage: string | null;
  i18n_explicit_choice_flag: boolean;
  i18n_explicit_choice_key: string;
  lang_query: string | null;
  i18n_language: string;
  i18n_resolvedLanguage: string | undefined;
  i18n_initialized: boolean;
  html_lang: string;
  html_data_lang: string | null;
  effective_site_language: string;
  detection_order_hint: string;
};

export type SiteLanguageDebugReport = SiteLanguageDebugPayload & {
  health: SiteLanguageHealth;
};

function safeConsole(
  level: "log" | "warn" | "error" | "info",
  ...args: unknown[]
): void {
  try {
    const g = globalThis as unknown as Record<string, unknown>;
    const con = g["console"] as Record<string, unknown> | undefined;
    if (!con) return;
    const fn = con[level];
    if (typeof fn !== "function") return;
    // Los métodos de console deben ejecutarse con this = console (apply(fn, con) evita "Illegal invocation").
    Reflect.apply(fn as (...a: unknown[]) => unknown, con, args);
  } catch {
    /* ignore */
  }
}

function persistLast(payload: SiteLanguageDebugReport): void {
  if (typeof window !== "undefined") {
    const key = `__${BRAND_STORAGE_PREFIX}LastLangDebug`;
    (window as unknown as Record<string, SiteLanguageDebugReport>)[key] = payload;
  }
}

function isNonLocalHost(href: string): boolean {
  try {
    const h = new URL(href).hostname;
    return h !== "localhost" && h !== "127.0.0.1" && !h.endsWith(".local");
  } catch {
    return true;
  }
}

function computeHealth(p: SiteLanguageDebugPayload): SiteLanguageHealth {
  const issues: string[] = [];
  const warnings: string[] = [];

  if (!p.i18n_initialized) {
    issues.push("i18n no está inicializado: las traducciones pueden fallar.");
  }

  if (!p.templateConfig) {
    if (isNonLocalHost(p.href)) {
      issues.push(
        "No hay window.__TEMPLATE_CONFIG__: el HTML no pasó por el Worker de Cloudflare o la inyección falló.",
      );
    } else {
      warnings.push("Sin __TEMPLATE_CONFIG__ (típico en Vite local sin Worker).");
    }
  }

  const i18nBase = (p.i18n_language || "").toLowerCase().split("-")[0] || "";
  if (p.usesSpanishDefaultForSite && i18nBase && i18nBase !== "es") {
    issues.push(
      `Política de sitio pide español (AR/cookie AR) pero i18n está en "${p.i18n_language}".`,
    );
  }

  const eff = (p.effective_site_language || "").toLowerCase();
  if (p.usesSpanishDefaultForSite && eff && !eff.startsWith("es")) {
    issues.push(
      `Locale efectivo del documento debería ser es* pero es "${p.effective_site_language}".`,
    );
  }

  if (
    p.lang_query &&
    p.usesSpanishDefaultForSite &&
    !p.lang_query.toLowerCase().startsWith("es")
  ) {
    warnings.push(
      `?lang=${p.lang_query} se ignora en zona español por defecto (esperado).`,
    );
  }

  if (p.templateConfig && p.user_country_cookie) {
    const t = normalizeCountryCode(p.templateConfig.country);
    const c = p.user_country_cookie;
    if (t && c && t !== "UNKNOWN" && c !== t) {
      warnings.push(
        `Cookie user_country (${c}) no coincide con template country (${p.templateConfig.country}).`,
      );
    }
  }

  if (p.usesSpanishDefaultForSite && p.i18n_explicit_choice_flag) {
    warnings.push(
      "Hay marca de idioma explícito en localStorage pero la zona es español por defecto (la preferencia no aplica).",
    );
  }

  return { ok: issues.length === 0, issues, warnings };
}

function finalizeReport(p: SiteLanguageDebugPayload): SiteLanguageDebugReport {
  const health = computeHealth(p);
  return { ...p, health };
}

function printReport(report: SiteLanguageDebugReport): void {
  const { health } = report;

  const brandTag = `[${BRAND_STORAGE_PREFIX}]`;
  
  if (!health.ok) {
    safeConsole(
      "error",
      `%c${brandTag} PROBLEMAS (${health.issues.length})`,
      "color:#dc2626;font-weight:bold;font-size:13px",
      health.issues,
    );
  } else {
    safeConsole(
      "info",
      `%c${brandTag} Estado: sin fallas detectadas (idioma/geo)`,
      "color:#16a34a;font-weight:bold",
    );
  }

  if (health.warnings.length > 0) {
    safeConsole(
      "warn",
      `%c${brandTag} Avisos`,
      "color:#ca8a04;font-weight:bold",
      health.warnings,
    );
  }

  const tc = report.templateConfig;
  if (tc) {
    // Orden = prioridad real de render (Index.tsx + resolveWorkerDrivenCta):
    // hideCta > showDual > showInstagram > showWhatsappCommunity > showCommunity
    // > showBirthday > showWishlist. `showDual` para AR cae en el bloque AR-dual
    // de Index.tsx ("❤️ Ser Exclusivo" → /ser-miembro); para no-AR promueve a
    // VideoTemplate (home `/` con video Mux + 2 botones, Stripe). Antes este
    // log NO contemplaba showDual y siempre mostraba "ninguno" aunque el
    // Worker emitiera showDual=true.
    const heroLabel = tc.hideCta
      ? "hideCta (sin botones)"
      : tc.showDual
        ? (tc.country === "AR"
            ? "dual → ❤️ Ser Exclusivo (/ser-miembro)"
            : "dual → VideoTemplate (home `/`)")
        : tc.showInstagram
          ? "Mejores Amigos → /instagram-mejores-amigos"
          : tc.showWhatsappCommunity
            ? "Comunidad WhatsApp → /whatsapp-broadcast"
            : tc.showCommunity
              ? "Exclusivo → /invitacion-premium"
              : tc.showBirthday
                ? "Comunidad → /bestfriend-instagram"
                : tc.showWishlist
                  ? "wishlist"
                  : "ninguno (revisá Worker / provincia / deploy)";
    safeConsole(
      "log",
      `%c${brandTag} CTA hero (Worker): ${heroLabel}`,
      "color:#0f766e;font-weight:bold;font-size:12px",
      {
        templateName: tc.templateName,
        country: tc.country,
        province: tc.province,
        provinceDetectionMethod: tc.provinceDetectionMethod ?? null,
        city: tc.city,
        hideCta: !!tc.hideCta,
        showDual: !!tc.showDual,
        showInstagram: !!tc.showInstagram,
        showWhatsappCommunity: !!tc.showWhatsappCommunity,
        showCommunity: !!tc.showCommunity,
        showBirthday: !!tc.showBirthday,
        showWishlist: !!tc.showWishlist,
      },
    );
  } else if (isNonLocalHost(report.href)) {
    safeConsole(
      "warn",
      `%c${brandTag} CTA hero (Worker)`,
      "color:#ea580c;font-weight:bold",
      "sin __TEMPLATE_CONFIG__ — el objeto i18n] detalle queda sin flags CTA",
    );
  }

  safeConsole(
    "log",
    `%c${brandTag} i18n] detalle`,
    "color:#7c3aed;font-weight:bold",
    report,
  );
}

export function isSiteDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (import.meta.env.VITE_SITE_DEBUG === "true") return true;
  } catch {
    /* ignore */
  }
  try {
    if (localStorage.getItem(STORAGE_KEY) === "1") return true;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  try {
    const d = new URLSearchParams(window.location.search).get("debug");
    if (d === "site" || d === "lang" || d === "i18n" || d === "1") {
      sessionStorage.setItem(SESSION_KEY, "1");
      return true;
    }
  } catch {
    /* ignore */
  }
  return import.meta.env.DEV;
}

export function buildSiteLanguagePayload(label: string): SiteLanguageDebugPayload {
  const i18nLang = i18n.language;
  const resolved = i18n.resolvedLanguage;
  return {
    at: `${label} · ${new Date().toISOString()}`,
    href: typeof window !== "undefined" ? window.location.href : "",
    templateConfig: getServerTemplateConfig(),
    usesSpanishDefaultForSite: usesSpanishDefaultForSite(),
    user_country_cookie: readEdgeCountryCookie(),
    edgeLanguageRaw: readEdgeLanguageRaw(),
    user_language_localStorage: readPersistedUserLanguage(),
    i18n_explicit_choice_flag: hasI18nUserLanguageChoice(),
    i18n_explicit_choice_key: I18N_USER_CHOICE_KEY,
    lang_query: readLangQueryParam(),
    i18n_language: i18nLang,
    i18n_resolvedLanguage: resolved,
    i18n_initialized: i18n.isInitialized,
    html_lang: typeof document !== "undefined" ? document.documentElement.lang : "",
    html_data_lang:
      typeof document !== "undefined"
        ? document.documentElement.getAttribute("data-lang")
        : null,
    effective_site_language: getEffectiveSiteLanguage(resolved || i18nLang),
    detection_order_hint:
      "querystringLang → spanishDefaultGeo → userExplicit → cloudflareEdge → persistedUserLanguage → navigator",
  };
}

/** Una vez por carga: resumen OK / PROBLEMAS + detalle. */
export function reportSiteLanguageConsoleOnce(): void {
  if (typeof window === "undefined" || autoConsoleReportDone) return;
  autoConsoleReportDone = true;
  const p = buildSiteLanguagePayload("LanguageProvider");
  const report = finalizeReport(p);
  persistLast(report);
  printReport(report);
}

export function captureSiteLanguageSnapshot(label: string): SiteLanguageDebugReport {
  const p = buildSiteLanguagePayload(label);
  const report = finalizeReport(p);
  persistLast(report);
  printReport(report);
  return report;
}

export function logSiteLanguageSnapshotIfEnabled(label: string): void {
  if (!isSiteDebugEnabled()) return;
  captureSiteLanguageSnapshot(label);
}

export function installSiteDebugGlobals(): void {
  if (typeof window === "undefined") return;
  const debugKey = `${BRAND_STORAGE_PREFIX}Debug`;
  const lastKey = `__${BRAND_STORAGE_PREFIX}LastLangDebug`;
  const w = window as unknown as Record<string, {
    enable: () => void;
    disable: () => void;
    log: () => SiteLanguageDebugReport;
    last: () => SiteLanguageDebugReport | undefined;
    help: () => void;
  }>;
  w[debugKey] = {
    enable: () => {
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      captureSiteLanguageSnapshot("manual enable");
    },
    disable: () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SESSION_KEY);
      } catch {
        /* ignore */
      }
    },
    log: () => captureSiteLanguageSnapshot(`${debugKey}.log()`),
    last: () =>
      (window as unknown as Record<string, SiteLanguageDebugReport | undefined>)[lastKey],
    help: () => {
      safeConsole(
        "log",
        `[${BRAND_STORAGE_PREFIX}] ${debugKey}.log() · buscar [${BRAND_STORAGE_PREFIX}] y la línea verde «CTA hero (Worker)» (flags visibles sin expandir {…})`,
      );
    },
  };
}
